import { useQuery } from '@tanstack/react-query';
import { differenceInDays, startOfDay } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { PaymentStatusType } from './useCasePaymentStatus';

// Report-specific status: extends the shared payment status with 'cancelled'
// (deals whose latest payment agreement was cancelled), surfaced by v_deal_payment_status.
export type ProcessoPaymentStatus = PaymentStatusType | 'cancelled';

export interface ProcessoRow {
  id: string;
  caseTypeName: string;
  visaType: string | null;
  caseStatus: string | null;
  dealId: string | null;
  dealName: string | null;
  contactName: string | null;
  paymentStatus: ProcessoPaymentStatus;
  overdueAmount: number;
  totalPending: number;
  maxDaysOverdue: number;
  closedDate: string | null;
  daysSinceClosed: number | null;
}

export type ProcessoTypeAgg = {
  type: string;
  total: number;
} & Record<ProcessoPaymentStatus, number>;

export interface ProcessosReportData {
  rows: ProcessoRow[];
  summary: {
    total: number;
    byPaymentStatus: Record<ProcessoPaymentStatus, number>;
  };
  byType: ProcessoTypeAgg[];
}

interface DealPaymentStatus {
  payment_status: ProcessoPaymentStatus;
  overdue_amount: number;
  total_pending: number;
  max_days_overdue: number;
}

const NO_TYPE_LABEL = 'Sem tipo';
const IN_CLAUSE_BATCH = 500;

const emptyStatusCounts = (): Record<ProcessoPaymentStatus, number> => ({
  up_to_date: 0,
  overdue: 0,
  partial: 0,
  settled: 0,
  no_payments: 0,
  cancelled: 0,
});

const NO_PAYMENTS: DealPaymentStatus = {
  payment_status: 'no_payments',
  overdue_amount: 0,
  total_pending: 0,
  max_days_overdue: 0,
};

export function useProcessosReport() {
  return useQuery({
    queryKey: ['processos-report'],
    queryFn: async (): Promise<ProcessosReportData> => {
      // 1. Fetch all cases with their type (catalog name) + deal (name, closed_date, contact)
      const { data: casesData, error: casesError } = await supabase
        .from('cases')
        .select(`
          id,
          status,
          created_at,
          visa_type,
          item:items!inner(
            catalog:catalog(name),
            deal:deals!inner(
              id,
              name,
              closed_date,
              contact:contacts!fk_deals_contact(first_name, last_name)
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (casesError) throw casesError;

      const cases = casesData || [];

      // 2. Distinct deal ids, then read pre-aggregated payment status per deal from the
      //    v_deal_payment_status view (mirrors the receivables/v_receivables_detail rule,
      //    computed server-side — avoids the PostgREST 1000-row cap on the payments table).
      const dealIds = Array.from(
        new Set(
          cases
            .map((c: any) => c.item?.deal?.id)
            .filter((id: string | undefined): id is string => Boolean(id))
        )
      );

      const statusByDeal = new Map<string, DealPaymentStatus>();

      for (let i = 0; i < dealIds.length; i += IN_CLAUSE_BATCH) {
        const batch = dealIds.slice(i, i + IN_CLAUSE_BATCH);
        const { data: statuses, error: statusError } = await (supabase
          .from('v_deal_payment_status' as any) as any)
          .select('deal_id, payment_status, overdue_amount, total_pending, max_days_overdue')
          .in('deal_id', batch);

        if (statusError) throw statusError;

        for (const s of statuses || []) {
          if (!s.deal_id) continue;
          statusByDeal.set(s.deal_id, {
            payment_status: s.payment_status as ProcessoPaymentStatus,
            overdue_amount: Number(s.overdue_amount) || 0,
            total_pending: Number(s.total_pending) || 0,
            max_days_overdue: Number(s.max_days_overdue) || 0,
          });
        }
      }

      // 3. Build one row per case with its payment status + days since closed
      const today = startOfDay(new Date());

      const rows: ProcessoRow[] = cases.map((caseItem: any): ProcessoRow => {
        const deal = caseItem.item?.deal;
        const contact = deal?.contact;
        const dealId: string | null = deal?.id || null;

        const contactName = contact
          ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || null
          : null;

        const pay = (dealId && statusByDeal.get(dealId)) || NO_PAYMENTS;

        const closedDate: string | null = deal?.closed_date || null;
        const referenceDate = closedDate || caseItem.created_at || null;
        const daysSinceClosed = referenceDate
          ? differenceInDays(today, startOfDay(new Date(referenceDate)))
          : null;

        return {
          id: caseItem.id,
          caseTypeName: caseItem.item?.catalog?.name || NO_TYPE_LABEL,
          visaType: caseItem.visa_type,
          caseStatus: caseItem.status,
          dealId,
          dealName: deal?.name || null,
          contactName,
          paymentStatus: pay.payment_status,
          overdueAmount: pay.overdue_amount,
          totalPending: pay.total_pending,
          maxDaysOverdue: pay.max_days_overdue,
          closedDate,
          daysSinceClosed,
        };
      });

      // 4. Aggregate summary + per-type breakdown
      const byPaymentStatus = emptyStatusCounts();
      const typeMap = new Map<string, ProcessoTypeAgg>();

      for (const row of rows) {
        byPaymentStatus[row.paymentStatus] += 1;

        let agg = typeMap.get(row.caseTypeName);
        if (!agg) {
          agg = {
            type: row.caseTypeName,
            total: 0,
            ...emptyStatusCounts(),
          };
          typeMap.set(row.caseTypeName, agg);
        }
        agg.total += 1;
        agg[row.paymentStatus] += 1;
      }

      const byType = Array.from(typeMap.values()).sort((a, b) => b.total - a.total);

      return {
        rows,
        summary: {
          total: rows.length,
          byPaymentStatus,
        },
        byType,
      };
    },
    staleTime: 60000,
  });
}
